import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ShipperAddress {
  id: string;
  shipper_id: string;
  label: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  default_contact_name: string | null;
  default_contact_phone: string | null;
  default_contact_email: string | null;
  default_instructions: string | null;
  use_count: number;
  last_used_at: string | null;
}

export function useShipperAddresses(shipperId: string | undefined) {
  const [addresses, setAddresses] = useState<ShipperAddress[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!shipperId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("direct_shipper_addresses" as any)
      .select("*")
      .eq("shipper_id", shipperId)
      .order("use_count", { ascending: false })
      .order("label", { ascending: true });
    setAddresses((data as any) || []);
    setLoading(false);
  }, [shipperId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createAddress = useCallback(
    async (payload: Partial<ShipperAddress>) => {
      if (!shipperId) return;
      await supabase
        .from("direct_shipper_addresses" as any)
        .insert({ ...payload, shipper_id: shipperId });
      await refresh();
    },
    [shipperId, refresh]
  );

  const updateAddress = useCallback(
    async (id: string, patch: Partial<ShipperAddress>) => {
      await supabase
        .from("direct_shipper_addresses" as any)
        .update(patch)
        .eq("id", id);
      await refresh();
    },
    [refresh]
  );

  const deleteAddress = useCallback(
    async (id: string) => {
      await supabase
        .from("direct_shipper_addresses" as any)
        .delete()
        .eq("id", id);
      await refresh();
    },
    [refresh]
  );

  return { addresses, loading, refresh, createAddress, updateAddress, deleteAddress };
}
